import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { intelligentSearchService } from '../../services/IntelligentSearchService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';

const IntelligentSearchDashboard = () => {
  const navigate = useNavigate();
  const { user, getRemainingRequests, getRequestLimit } = useAuth();
  const [stats, setStats] = useState(null);
  const [searchAreas, setSearchAreas] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    
    try {
      // Load statistics
      const { data: statsData } = await intelligentSearchService.getIntelligentSearchStats();
      if (statsData) {
        setStats(statsData);
      }

      // Load recent search areas
      const { data: areasData } = await intelligentSearchService.getIntelligentSearchAreas({ 
        page_size: 5 
      });
      if (areasData) {
        setSearchAreas(areasData.search_areas || []);
      }

      // Load recent sessions
      const { data: sessionsData } = await intelligentSearchService.getIntelligentSearchSessions({ 
        page_size: 5 
      });
      if (sessionsData) {
        setSessions(sessionsData.sessions || []);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
    
    setLoading(false);
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'blue', onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon, color = 'blue', onClick, badge }) => (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-start">
        <div className={`p-3 rounded-lg bg-${color}-100 flex-shrink-0`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4 flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {badge && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <div className="ml-4 flex-shrink-0">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Intelligent Search Dashboard</h1>
              <p className="text-sm text-gray-600">AI-powered location optimization and business discovery</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/intelligent-search')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Start New Search
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Main Dashboard
              </button>
            </div>
          </div></div>
     </div>

     <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
       {/* Stats Overview */}
       <div className="mb-8">
         <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
           <StatCard
             title="API Requests Remaining"
             value={getRemainingRequests()}
             subtitle={`of ${getRequestLimit()} total`}
             icon="🔄"
             color="blue"
           />
           <StatCard
             title="Search Areas Created"
             value={stats?.total_areas || 0}
             subtitle={`${stats?.ready_areas || 0} ready to search`}
             icon="🗺️"
             color="green"
           />
           <StatCard
             title="Circles Generated"
             value={stats?.total_circles_generated || 0}
             subtitle={`Avg ${stats?.avg_circles_per_area || 0} per area`}
             icon="⭕"
             color="purple"
           />
           <StatCard
             title="Searches Completed"
             value={stats?.total_searches_completed || 0}
             subtitle={`${stats?.avg_places_per_circle || 0} avg places/circle`}
             icon="✅"
             color="orange"
           />
         </div>
       </div>

       {/* Quick Actions */}
       <div className="mb-8">
         <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
           <QuickActionCard
             title="Start Intelligent Search"
             description="Use AI-powered location optimization to find businesses efficiently"
             icon="🚀"
             color="blue"
             onClick={() => navigate('/intelligent-search')}
           />
           <QuickActionCard
             title="View Search Areas"
             description="Manage your created search areas and view generated circles"
             icon="🗺️"
             color="green"
             onClick={() => navigate('/intelligent-search/areas')}
             badge={stats?.pending_areas > 0 ? `${stats.pending_areas} pending` : null}
           />
           <QuickActionCard
             title="Search History"
             description="Review past intelligent searches and their performance"
             icon="📊"
             color="purple"
             onClick={() => navigate('/intelligent-search/history')}
             badge={sessions.length > 0 ? `${sessions.length} recent` : null}
           />
         </div>
       </div>

       {/* Recent Search Areas */}
       <div className="mb-8">
         <div className="flex items-center justify-between mb-4">
           <h2 className="text-lg font-medium text-gray-900">Recent Search Areas</h2>
           <button
             onClick={() => navigate('/intelligent-search/areas')}
             className="text-sm text-blue-600 hover:text-blue-800 font-medium"
           >
             View All →
           </button>
         </div>
         
         {searchAreas.length === 0 ? (
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
             <div className="text-gray-400 mb-4">
               <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
               </svg>
             </div>
             <h3 className="text-lg font-medium text-gray-900 mb-2">No Search Areas Yet</h3>
             <p className="text-gray-600 mb-4">Create your first intelligent search area to get started</p>
             <button
               onClick={() => navigate('/intelligent-search')}
               className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
             >
               Create Search Area
             </button>
           </div>
         ) : (
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
             <div className="divide-y divide-gray-200">
               {searchAreas.map((area) => (
                 <div key={area.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/intelligent-search/areas/${area.id}`)}>
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <h3 className="text-sm font-medium text-gray-900">{area.area_name}</h3>
                       <div className="flex items-center space-x-4 mt-1">
                         <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${intelligentSearchService.getStatusColor(area.status)}-100 text-${intelligentSearchService.getStatusColor(area.status)}-800`}>
                           {intelligentSearchService.formatSearchAreaStatus(area.status)}
                         </span>
                         <span className="text-xs text-gray-500">
                           {area.included_types?.length || 0} business types
                         </span>
                         <span className="text-xs text-gray-500">
                           Created {new Date(area.created_at).toLocaleDateString()}
                         </span>
                       </div>
                     </div>
                     <div className="text-right">
                       <div className="text-sm font-medium text-gray-900">
                         {area.optimized_circles?.length || 0} circles
                       </div>
                       {area.status === 'completed' && (
                         <div className="text-xs text-gray-500">Search complete</div>
                       )}
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           </div>
         )}
       </div>

       {/* Recent Search Sessions */}
       <div className="mb-8">
         <div className="flex items-center justify-between mb-4">
           <h2 className="text-lg font-medium text-gray-900">Recent Search Sessions</h2>
           <button
             onClick={() => navigate('/intelligent-search/history')}
             className="text-sm text-blue-600 hover:text-blue-800 font-medium"
           >
             View All →
           </button>
         </div>
         
         {sessions.length === 0 ? (
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
             <div className="text-gray-400 mb-4">
               <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
               </svg>
             </div>
             <h3 className="text-lg font-medium text-gray-900 mb-2">No Search Sessions Yet</h3>
             <p className="text-gray-600">Execute your first intelligent search to see results here</p>
           </div>
         ) : (
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
             <div className="divide-y divide-gray-200">
               {sessions.map((session) => (
                 <div key={session.id} className="p-4 hover:bg-gray-50">
                   <div className="flex items-center justify-between">
                     <div className="flex-1">
                       <h3 className="text-sm font-medium text-gray-900">
                         {session.search_area?.area_name || 'Unknown Area'}
                       </h3>
                       <div className="flex items-center space-x-4 mt-1">
                         <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${intelligentSearchService.getStatusColor(session.status)}-100 text-${intelligentSearchService.getStatusColor(session.status)}-800`}>
                           {session.status}
                         </span>
                         <span className="text-xs text-gray-500">
                           {session.circles_searched} circles searched
                         </span>
                         <span className="text-xs text-gray-500">
                           {new Date(session.created_at).toLocaleDateString()}
                         </span>
                       </div>
                     </div>
                     <div className="text-right">
                       <div className="text-sm font-medium text-gray-900">
                         {session.total_places_found} places found
                       </div>
                       <div className="text-xs text-gray-500">
                         {session.api_requests_made} API requests used
                       </div>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           </div>
         )}
       </div>

       {/* Performance Insights */}
       {stats && stats.total_searches_completed > 0 && (
         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
           <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Insights</h3>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             <div className="text-center">
               <div className="text-2xl font-bold text-blue-600 mb-2">
                 {stats.avg_circles_per_area?.toFixed(1) || '0'}
               </div>
               <div className="text-sm text-gray-500">Average Circles per Area</div>
             </div>
             <div className="text-center">
               <div className="text-2xl font-bold text-green-600 mb-2">
                 {stats.avg_places_per_circle?.toFixed(1) || '0'}
               </div>
               <div className="text-sm text-gray-500">Average Places per Circle</div>
             </div>
             <div className="text-center">
               <div className="text-2xl font-bold text-purple-600 mb-2">
                 {((stats.total_searches_completed / (stats.total_areas || 1)) * 100).toFixed(0)}%
               </div>
               <div className="text-sm text-gray-500">Search Completion Rate</div>
             </div>
           </div>
         </div>
       )}

       {/* Getting Started Guide */}
       {(!stats || stats.total_areas === 0) && (
         <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
           <div className="flex items-start">
             <div className="flex-shrink-0">
               <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
               </svg>
             </div>
             <div className="ml-3 flex-1">
               <h3 className="text-lg font-medium text-blue-900 mb-2">
                 Welcome to Intelligent Search!
               </h3>
               <p className="text-blue-700 mb-4">
                 Get started with AI-powered location optimization to discover businesses more efficiently than manual searching.
               </p>
               <div className="space-y-2">
                 <div className="flex items-center text-sm text-blue-700">
                   <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">1</span>
                   <span>Enter a location (city, region, or area)</span>
                 </div>
                 <div className="flex items-center text-sm text-blue-700">
                   <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">2</span>
                   <span>Configure business types and AI optimization</span>
                 </div>
                 <div className="flex items-center text-sm text-blue-700">
                   <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">3</span>
                   <span>Review AI-generated optimized circles</span>
                 </div>
                 <div className="flex items-center text-sm text-blue-700">
                   <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-3">4</span>
                   <span>Execute search and save results</span>
                 </div>
               </div>
               <div className="mt-4">
                 <button
                   onClick={() => navigate('/intelligent-search')}
                   className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                 >
                   Start Intelligent Search →
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   </div>
 );
};

export default IntelligentSearchDashboard;